import { useState } from "react";
import { CircuitBoard, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dot } from "@/components/ui/dot";
import { Notify, toast } from "@/components/ui/notify";
import { isApiClientError } from "@/lib/api-client";
import type { DeviceSummary } from "@/features/devices/hooks";
import { useDeleteCard, type CardSummary } from "@/features/cards/hooks";
import { useDeleteSensor, type SensorSummary } from "@/features/sensors/hooks";
import { Breadcrumb } from "./Breadcrumb";
import { ChildRow } from "./ChildRow";
import { UuidLine } from "./UuidLine";
import { CardEditForm } from "./CardEditForm";
import { NewSensorForm } from "./NewSensorForm";
import { SENSOR_STATUS_TONE } from "./sensor-display";

interface CardDetailPaneProps {
  device: DeviceSummary;
  card: CardSummary;
  sensors: SensorSummary[];
  onSelectClp: () => void;
  onSelectSensor: (sensorId: string) => void;
  onDirtyChange: (dirty: boolean) => void;
  onDeleted: () => void;
}

export function CardDetailPane({
  device,
  card,
  sensors,
  onSelectClp,
  onSelectSensor,
  onDirtyChange,
  onDeleted,
}: CardDetailPaneProps) {
  const [showNew, setShowNew] = useState(false);
  const delCard = useDeleteCard(device.id);
  const delSensor = useDeleteSensor();

  async function onDelete() {
    const ok = await Notify.confirm({
      variant: "danger",
      title: `Excluir cartão "${card.code}"?`,
      confirmLabel: "Excluir",
      typeToConfirm: card.code,
    });
    if (!ok) return;
    try {
      await delCard.mutateAsync(card.id);
      toast.success("Cartão excluído.");
      onDeleted();
    } catch (err) {
      toast.error("Falha ao excluir.", {
        description: isApiClientError(err) ? err.error.message : "Erro inesperado.",
      });
    }
  }

  async function onDeleteSensor(id: string, code: string) {
    const ok = await Notify.confirm({
      variant: "danger",
      title: `Excluir sensor "${code}"?`,
      confirmLabel: "Excluir",
      typeToConfirm: code,
    });
    if (!ok) return;
    try {
      await delSensor.mutateAsync(id);
      toast.success("Sensor excluído.");
    } catch (err) {
      toast.error("Falha ao excluir.", {
        description: isApiClientError(err) ? err.error.message : "Erro inesperado.",
      });
    }
  }

  const ordered = [...sensors].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  return (
    <div className="space-y-5">
      <Breadcrumb
        parts={[
          { label: device.code, mono: true, onClick: onSelectClp },
          { label: card.code, mono: true },
        ]}
      />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[12px] text-text-secondary">
            <CircuitBoard className="h-3.5 w-3.5" /> Cartão de I/O
          </div>
          <h2 className="font-mono text-[20px] font-semibold text-text-primary">{card.code}</h2>
          <UuidLine uuid={card.id} />
        </div>
        <Button variant="ghost" size="sm" onClick={() => void onDelete()}>
          <Trash2 className="h-4 w-4" /> Excluir cartão
        </Button>
      </div>

      <section className="space-y-3">
        <div className="text-[13px] font-medium text-text-secondary">Configuração</div>
        <CardEditForm deviceId={device.id} card={card} onDirtyChange={onDirtyChange} />
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-[13px] font-medium text-text-secondary">
            Sensores / posições <span className="text-text-tertiary">· {sensors.length}</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowNew((v) => !v)}>
            {showNew ? "Fechar" : "Nova posição"}
          </Button>
        </div>
        {showNew && (
          <NewSensorForm deviceId={device.id} card={card} onDone={() => setShowNew(false)} />
        )}
        <div className="space-y-1.5">
          {ordered.map((s) => (
            <ChildRow
              key={s.id}
              icon={<Dot tone={SENSOR_STATUS_TONE[s.status]} halo={false} size={8} />}
              code={s.code}
              meta={`${s.name}${s.position != null ? ` · pos ${s.position}` : ""}`}
              onOpen={() => onSelectSensor(s.id)}
              onDelete={() => void onDeleteSensor(s.id, s.code)}
            />
          ))}
          {sensors.length === 0 && (
            <p className="text-[13px] text-text-secondary">Nenhuma posição cadastrada.</p>
          )}
        </div>
      </section>
    </div>
  );
}
